
import { LocationModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { locationsStore } from '@/libs/locations/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Watch, Ref } from 'vue-property-decorator';
import LocationDialog from '../components/LocationDialog.vue';
import { Guid } from '@/libs/common/functions/guid';

@Component({
    components: {
        LocationDialog,
    },
})
export default class Locations extends Page {
    private depthMap: { [id: string]: number } = {};
    get headers() {
        return [
            { text: this.$i18n.t('locations.code'), value: 'code', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.name'), value: 'name', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.canPlanShifts'), value: 'canPlanShifts', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.parental'), value: 'id', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.costs'), value: 'costs', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false },
        ];
    }
    itemsPerPage = 10;

    @Prop() tenantId!: string;
    @Ref() dialog!: LocationDialog;

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    get loading(): boolean {
        return locationsStore.useGetter(CrudGetter.Pending);
    }

    get locations(): LocationModel[] {
        const items: LocationModel[] = [...locationsStore.useGetter(CrudGetter.Data)];
        if (!items || items.length === 0) {
            return items;
        }

        const idToChildren = new Map<string, LocationModel[]>();
        const idToItem = new Map<string, LocationModel>();
        const result: LocationModel[] = [];
        this.depthMap = {};

        for (const item of items) {
            if (item.id) {
                idToItem.set(item.id, item);
            }
        }

        for (const item of items) {
            const parentId = item.parentalLocationId ?? undefined;
            if (parentId && idToItem.has(parentId)) {
                const arr = idToChildren.get(parentId) ?? [];
                arr.push(item);
                idToChildren.set(parentId, arr);
            }
        }

        const sortByName = (a: LocationModel, b: LocationModel) => (a.name ?? '').localeCompare(b.name ?? '');
        for (const arr of idToChildren.values()) {
            arr.sort(sortByName);
        }

        const roots = items.filter((x) => !x.parentalLocationId || !idToItem.has(x.parentalLocationId));
        roots.sort(sortByName);

        const visit = (node: LocationModel, depth: number) => {
            result.push(node);
            if (node.id) {
                this.depthMap[node.id] = depth;
            }
            const children: LocationModel[] = node.id ? idToChildren.get(node.id) ?? [] : [];
            for (const child of children) {
                visit(child, depth + 1);
            }
        };

        for (const r of roots) {
            visit(r, 0);
        }

        return result;
    }

    get totalRows(): number {
        return ({ ...locationsStore.useGetter(CrudGetter.DataStats) } as CrudDataStats)?.rowCount;
    }

    mounted() {
        this.itemsPerPage = window.localStorage.amsItemsPerPage ? Number(window.localStorage.amsItemsPerPage) : 10;
        this.load();
    }

    load() {
        locationsStore.dispatch(CrudAction.GetAll, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [];
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<LocationModel>({ field: 'name', index: 1, order: 'asc' }, this.getFilters());
    }

    open(item: LocationModel) {
        this.dialog.open(item.id!);
    }

    createItemClick() {
        this.dialog.open(Guid.EmptyGuid());
    }

    getParentalLocationById(id: string | null): LocationModel | undefined {
        return this.locations.find((x) => x.id == id);
    }

    formatCost(v: number | null | undefined, hasParent: boolean) {
        return v == null ? (hasParent ? (this.$t('costs.inheritedFromParent') as any) : '--') : v;
    }

    getDepth(item: LocationModel): number {
        return item.id ? this.depthMap[item.id] ?? 0 : 0;
    }
}
